import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import ReCAPTCHA from 'react-google-recaptcha';
import { assign, keys, values } from 'lodash-es';
import helpers from '../../../utils/helpers';
import SimpleCheckbox from '../../../components/Checkbox/SimpleCheckbox';
import axios from '../../../configs/axios';
import Button from '../../../components/Buttons/Button';

const labelTextTemplate = (
  <span className="label-with-links">
    By checking this box, you agree to Apptopia&apos;s <a target="_blank" rel="noopener noreferrer" href="https://apptopia.com/privacy">Privacy Notice</a>, <a target="_blank" rel="noopener noreferrer" href="https://apptopia.com/pricing">Pricing Policy</a>, <a target="_blank" rel="noopener noreferrer" href="https://apptopia.com/terms">Terms of Service</a>, <a target="_blank" rel="noopener noreferrer" href="https://apptopia.com/data-usage">Data Usage</a>, and to receive the occasional product and marketing email from us.
  </span>
);

const Form = ({ handleStep, sitekey }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    companyName: '',
    checked: false,
  });
  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
    companyName: '',
    checked: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const recaptchaRef = useRef(null);

  const {
    firstName,
    lastName,
    email,
    companyName,
    checked,
  } = formData;

  const validateError = (field) => {
    const key = keys(field)[0];
    const value = values(field)[0];
    switch (key) {
      case 'firstName':
        return { firstName: !value.trim() ? 'Please input valid First name' : '' };
      case 'lastName':
        return { lastName: !value.trim() ? 'Please input valid Last name' : '' };
      case 'companyName':
        return { companyName: !value.trim() ? 'Please input valid Job title' : '' };
      case 'email':
        return { email: (!value || !helpers.validateEmail(value) || value.includes('+')) ? 'Please enter a valid email address' : '' };
      case 'checked':
        return { checked: !value ? 'Please accept Terms of Service' : '' };
      default:
        return {};
    }
  };

  const validateForm = () => {
    const errorsState = { ...errors };

    assign(errorsState, validateError({ firstName }));
    assign(errorsState, validateError({ lastName }));
    assign(errorsState, validateError({ companyName }));
    assign(errorsState, validateError({ email }));
    assign(errorsState, validateError({ checked }));

    setErrors(errorsState);
  };

  const isErrorsPresent = () => (
    errors.email.length > 0
    || errors.firstName.length > 0
    || errors.lastName.length > 0
    || errors.companyName.length > 0
    || errors.checked.length > 0
  );

  const isFormFilled = () => (
    firstName.length > 0
    && lastName.length > 0
    && email.length > 0
    && companyName.length > 0
    && checked
  );

  const submitDisabled = () => !isFormFilled() || isErrorsPresent();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!submitDisabled() && !isSubmitting) {
      setIsSubmitting(true);
      recaptchaRef.current.reset();
      recaptchaRef.current.executeAsync().then((token) => {
        validateForm();
        if (!submitDisabled()) {
          axios.post('/press_users',
            {
              token,
              user: {
                first_name: firstName,
                last_name: lastName,
                email,
                company_name: companyName,
              },
            },
          ).then(() => {
            setIsSubmitting(false);
            handleStep('success');
          }).catch(({ response }) => {
            setIsSubmitting(false);
            if (response.status === 403) {
              const { email_error } = response.data;
              setErrors({ ...errors, email: email_error });
            }
          });
        }
      });
    }
  };

  const handleFieldChange = (field) => {
    setFormData({ ...formData, ...field });
    setErrors({ ...errors, ...validateError(field) });
  };

  return (
    <form onSubmit={handleSubmit} className="user-report-form">
      <div className="row m-a-0 p-a-0 names-row">
        <div className="col-md-6">
          <fieldset className={cx('form-group pos-rel mb-sm', { 'has-error': Boolean(errors.firstName) })}>
            <input
              className="form-control"
              placeholder="First Name"
              type="text"
              onChange={e => handleFieldChange({ firstName: e.target.value })}
              value={firstName}
            />
            {errors.firstName &&
            <div className="error-message">
              {errors.firstName}
            </div>
            }
          </fieldset>
        </div>
        <div className="col-md-6">
          <fieldset className={cx('form-group pos-rel mb-sm', { 'has-error': Boolean(errors.lastName) })}>
            <input
              className="form-control"
              placeholder="Last Name"
              type="text"
              onChange={e => handleFieldChange({ lastName: e.target.value })}
              value={lastName}
            />
            {errors.lastName &&
            <div className="error-message">
              {errors.lastName}
            </div>
            }
          </fieldset>
        </div>
      </div>
      <div className="row m-a-0 p-a-0 w-100">
        <div className="col-md-6">
          <fieldset className={cx('form-group pos-rel', { 'has-error': Boolean(errors.email) })}>
            <input
              className="form-control"
              placeholder="Business Email"
              type="text"
              onChange={e => handleFieldChange({ email: e.target.value })}
              value={email}
            />
            {errors.email &&
            <div className="error-message">
              {errors.email}
            </div>
            }
          </fieldset>
        </div>
        <div className="col-md-6">
          <fieldset className={cx('form-group pos-rel mb-sm dropdown-job', { 'has-error': Boolean(errors.companyName) })}>
            <input
              className="form-control"
              placeholder="Publication"
              type="text"
              onChange={e => handleFieldChange({ companyName: e.target.value })}
              value={companyName}
            />
            {errors.companyName &&
            <div className="error-message">
              {errors.companyName}
            </div>
            }
          </fieldset>
        </div>
      </div>
      <div className="row m-a-0 p-a-0 w-100">
        <div className="col-md-12 justify-content-center align-items-center">
          <SimpleCheckbox
            id="agreement-check"
            label={labelTextTemplate}
            checked={checked}
            gap="9px"
            onChange={active => handleFieldChange({ checked: active })}
          />
          {errors.checked &&
          <div className="text-red text-12 p-t-xxs">
            {errors.checked}
          </div>
          }
        </div>
      </div>
      <div className="row m-a-0 p-a-0 w-100">
        <div className="d-flex col-md-12 p-a-0 justify-content-center align-items-center">
          <Button
            className="report-analysis-submit-btn"
            loading={isSubmitting}
            size="lg"
            disabled={submitDisabled()}
            type="submit"
          >
            Submit
          </Button>
          <ReCAPTCHA
            ref={recaptchaRef}
            size="invisible"
            sitekey={sitekey}
          />
        </div>
      </div>
    </form>
  );
};

Form.propTypes = {
  handleStep: PropTypes.func.isRequired,
  sitekey: PropTypes.string.isRequired,
};

export default Form;
